import React from 'react';
import {
  Box, ScrollView, HStack, Text, Pressable, Icon,
} from '@gluestack-ui/themed';
import { Calendar } from 'lucide-react-native';
import { useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { useCalendarBadges } from '../EventCalendar/hooks/useCalendarBadges';
import ModuleList from './modules/ModuleList';
import SubmissionList from './submissions/SubmissionList';
import TaskList from './tasks/TaskList';

interface SectionHeaderProps {
  title: string;
  visible: boolean;
}

/** Section header component - matches LecturerNavigation style */
const SectionHeader: React.FC<SectionHeaderProps> = ({ title, visible }) => {
  if (!visible) return null;
  return (
    <Box px="$4" pt="$4" pb="$1.5">
      <Text size="2xs" fontWeight="bold" color="$textSubtle" textTransform="uppercase" letterSpacing="$md">
        {title}
      </Text>
    </Box>
  );
};

interface SidebarItemProps {
  label: string;
  icon: React.ElementType;
  isActive: boolean;
  onPress: () => void;
  visible: boolean;
  badge?: number;
  hasOverdue?: boolean;
}

/** Sidebar menu item - matches LecturerNavigation RailItem style */
const SidebarItem: React.FC<SidebarItemProps> = ({
  label, icon: IconComponent, isActive, onPress, visible, badge = 0, hasOverdue = false,
}) => {
  if (!visible) return null;
  return (
    <Pressable
      onPress={onPress}
      p="$2.5"
      px="$3"
      ml={isActive ? '$1' : '$1.5'}
      mr="$1.5"
      my="$0.5"
      borderLeftWidth={isActive ? 3 : 0}
      borderLeftColor={isActive ? '$brandBase' : 'transparent'}
      bg="transparent"
      opacity={isActive ? 1 : 0.8}
      testID={`sidebar-item-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <HStack alignItems="center" space="md">
        <Icon as={IconComponent} size="sm" color={isActive ? '$brandBase' : '$textSubtle'} />
        <Text
          size="sm"
          fontWeight={isActive ? 'semibold' : 'medium'}
          color={isActive ? '$textHeading' : '$textBody'}
          flex={1}
          numberOfLines={1}
        >
          {label}
        </Text>
        {badge > 0 && (
          <Box
            bg={hasOverdue ? '$statusError' : '$brandBase'}
            borderRadius="$full"
            minWidth={20}
            height={20}
            px="$1.5"
            alignItems="center"
            justifyContent="center"
          >
            <Text size="2xs" fontWeight="bold" color="$textInverted">
              {badge}
            </Text>
          </Box>
        )}
      </HStack>
    </Pressable>
  );
};

interface Module {
  id: string;
  title: string;
}

interface SidebarProps {
  show: 'modules' | 'tasks' | 'submissions';
  module: Module | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  submissions: any[];
  isLoading?: boolean;
  /**
   * Optional guard invoked before a task in the sidebar is selected. Receives a
   * `proceed` callback that performs the actual navigation. If omitted, task
   * selection happens immediately (preserves existing behavior for other hosts).
   */
  onBeforeTaskSelect?: (proceed: () => void) => void;
}

const Sidebar: React.FC<SidebarProps> = React.memo(({
  show,
  module,
  submissions,
  isLoading = false,
  onBeforeTaskSelect,
}) => {
  const { t } = useTranslation();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const navigation = useNavigation<any>();

  // Get planning gaps for badge (tasks + mandates only, no slots)
  const { hasEventTasks, gapCount, hasCriticalGaps } = useCalendarBadges();

  // Consistent sidebar styling matching outer LecturerNavigation rail
  const sidebarStyle = {
    width: 350,
    backgroundColor: '$bgApp',
    borderRightWidth: 1,
    borderRightColor: '$borderLight',
    height: '100%' as const,
  };

  // For modules view, show the sectioned layout
  if (show === 'modules') {
    return (
      <Box sx={sidebarStyle} testID="lecturer-sidebar">
        <ScrollView sx={{ _contentContainerStyle: { flex: 1, pt: '$2' } }}>
          {/* CALENDAR Section */}
          <SectionHeader title="Calendar" visible={hasEventTasks} />
          <SidebarItem
            label={t('lecturer.sidebar.titles.meetingSlots')}
            icon={Calendar}
            badge={gapCount}
            hasOverdue={hasCriticalGaps}
            isActive={false}
            onPress={() => navigation.navigate('EventCalendar')}
            visible={hasEventTasks}
          />

          {/* MODULES Section */}
          <SectionHeader title="Modules" visible />
          <ModuleList visible />
        </ScrollView>
      </Box>
    );
  }

  // For tasks view - TaskList owns its own SectionList scroller, so no outer ScrollView needed.
  // Wrapping in ScrollView collapses flex:1 in TaskList's loading/empty states (spinner top-left).
  if (show === 'tasks') {
    return (
      <Box sx={sidebarStyle} testID="lecturer-sidebar">
        <TaskList visible isLoading={isLoading} onBeforeSelect={onBeforeTaskSelect} />
      </Box>
    );
  }

  // For submissions view
  if (show === 'submissions') {
    return (
      <Box sx={sidebarStyle} testID="lecturer-sidebar">
        {/* Sidebar Header for submissions */}
        <Box px="$4" py="$3" borderBottomWidth={1} borderBottomColor="$borderLight">
          <HStack alignItems="center" space="sm">
            <Text size="sm" fontWeight="bold" color="$textHeading">
              {t('lecturer.sidebar.titles.submissions')}
              {module?.title && `: ${module.title}`}
            </Text>
          </HStack>
        </Box>

        <ScrollView px={0} py="$2" flex={1}>
          <SubmissionList visible module={module} submissions={submissions} isLoading={isLoading} />
        </ScrollView>
      </Box>
    );
  }

  return null;
});

export default Sidebar;
